/* eslint-disable func-names */
import dayjs from 'dayjs';
import { isValidPhoneNumber } from 'libphonenumber-js';
import * as Yup from 'yup';

import {
  checkFutureDate,
  checkValidDate,
  isValidDate,
  isValidFullName,
  isValidName,
  isValidZipCode,
} from '../../../../../Utils/validation';
import { PatientFormFiveSubmit } from '../../utils/types';

export const validationSchema = (
  patientForm: PatientFormFiveSubmit,
  paymentMethod: string,
  addSecondaryInsurance: boolean,
  isSameAddress: boolean,
  isOtherCarrierIdIsSelected: boolean,
): any => {
  if (isOtherCarrierIdIsSelected) {
    return Yup.object().shape({
      insurance_carrier_id: Yup.number()
        .typeError('Insurance carrier ID must be a number')
        .required('Insurance carrier ID is required'),
    });
  }
  if (paymentMethod === 'Pay with insurance') {
    return Yup.object().shape({
      insurance_carrier_id: Yup.number().test('insurance_carrier_id', 'Insurance carrier ID is required', (value) => {
        return patientForm.insurance_through !== 'none' && paymentMethod === 'Pay with insurance' ? value !== 0 : true;
      }),

      member_id: Yup.string().test('member_id', 'Member ID is required', (value) => {
        return patientForm.insurance_through !== 'none' && paymentMethod === 'Pay with insurance'
          ? value?.trim() !== ''
          : true;
      }),
      group_number: Yup.string().nullable().nullable(),
      insurance_phone_number: Yup.string()
        .test('iPhoneNumber', 'Insurance phone number is required', (value) => {
          if (patientForm.insurance_through !== 'none' && paymentMethod === 'Pay with insurance') {
            if (!value) {
              return false;
            }
          }
          return true;
        })
        .test('test-name', 'Enter Valid Phone Number', (value) => {
          if (value && !isValidPhoneNumber(value)) {
            return false;
          }
          return true;
        }),
      insurance_address: Yup.string().test('insurance_address', 'Insurance Address is required', (value) => {
        return patientForm.insurance_through !== 'none' && paymentMethod === 'Pay with insurance'
          ? value?.trim() !== ''
          : true;
      }),

      insurance_city: Yup.string().test('insurance_city', 'Insurance City is required', (value) => {
        return patientForm.insurance_through !== 'none' && paymentMethod === 'Pay with insurance'
          ? value?.trim() !== ''
          : true;
      }),
      insurance_state: Yup.string().test('insurance_state', 'Insurance State is required', (value) => {
        return patientForm.insurance_through !== 'none' && paymentMethod === 'Pay with insurance'
          ? value?.trim() !== ''
          : true;
      }),
      insurance_zip_code: Yup.string()
        .test('test-name', 'Not valid insurance zipcode', (value) => {
          if (value && !isValidZipCode(value)) {
            return false;
          }
          return true;
        })
        .test('insurance_zip_code', 'Insurance Zip Code is required', (value) => {
          return patientForm.insurance_through !== 'none' && paymentMethod === 'Pay with insurance'
            ? value?.trim() !== ''
            : true;
        }),
      state_funded_coverage_status: Yup.string().when('insurance_through', {
        is: (val: string) => val && val.trim() !== '' && val !== 'none' && paymentMethod === 'Pay with insurance',
        then: Yup.string().required(
          'Do you currently have insurance coverage through a state-funded program such as Medicaid?',
        ),
        otherwise: Yup.string(),
      }),
      insurance_through: Yup.string().test(
        'insurance_through',
        'Please make a selection for “Are you the policy holder for this insurance?”',
        (value) => {
          return paymentMethod === 'Pay with insurance' ? value?.trim() !== '' : true;
        },
      ),
      insurance_first_name: Yup.string().when(['insurance_through'], {
        is: (val: string) => {
          return val !== 'none' && val.trim() !== '' && val !== undefined && val !== 'myself';
        },
        then: Yup.string().required('Insurance Holder First Name is required'),
      }),
      insurance_last_name: Yup.string().when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val.trim() !== '' && val !== undefined && val !== 'myself',
        then: Yup.string().required('Insurance Holder Last Name is required'),
      }),
      insurance_date_of_birth: Yup.string()
        .when(['insurance_through'], {
          is: (val: string) => val !== 'none' && val.trim() !== '' && val !== undefined && val !== 'myself',
          then: Yup.string().required('Insurance Holder Date of birth is required'),
        })
        .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value) => {
          const formattedDate = value ? dayjs(value).format('MM/DD/YYYY') : '';
          if (value && !isValidDate(formattedDate)) {
            return false;
          }
          if (value) {
            const values = formattedDate?.split('/');
            if (!checkValidDate(values[0], values[1], values[2])) {
              return false;
            }
          }
          return true;
        })
        .test('test-future', 'The Date of Birth can not be in the future.', (value) => {
          if (value) {
            const formattedDate = dayjs(value).format('MM/DD/YYYY');
            const values = formattedDate?.split('/');
            if (!checkFutureDate(values[0], values[1], values[2])) {
              return false;
            }
          }
          return true;
        }),
      insurance_gender: Yup.string().when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val.trim() !== '' && val !== undefined && val !== 'myself',
        then: Yup.string()
          .required('Insurance Holder Gender is required')
          .test('test-name', 'Enter Valid Gender', (value) => {
            if (value && !isValidName(value)) {
              return false;
            }
            return true;
          }),
        otherwise: Yup.string(),
      }),
      insurance_address1: Yup.string().when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val.trim() !== '' && val !== undefined && val !== 'myself',
        then: Yup.string().required('Insurance Holder Address is required'),
      }),
      insurance_city1: Yup.string().when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val.trim() !== '' && val !== undefined && val !== 'myself',
        then: Yup.string().required('Insurance Holder city Required'),
      }),
      insurance_state1: Yup.string().when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val.trim() !== '' && val !== undefined && val !== 'myself',
        then: Yup.string().required('Insurance Holder state Required'),
      }),
      insurance_zip_code1: Yup.string()
        .when(['insurance_through'], {
          is: (val: string) => val !== 'none' && val.trim() !== '' && val !== undefined && val !== 'myself',
          then: Yup.string().required('Insurance Holder ZipCode is required'),
        })
        .test('test-name', 'Not valid zipcode', (value) => {
          if (value && !isValidZipCode(value)) {
            return false;
          }
          return true;
        }),
    }) as any;
  }
  if (addSecondaryInsurance && paymentMethod === 'Pay with insurance') {
    return Yup.object().shape({
      secondary_insurance_carrier_id: Yup.number().required('Secondary Insurance carrier ID is required'),

      secondary_member_id: Yup.string().required('Secondary Member ID is required'),

      secondary_group_number: Yup.string().nullable(),

      secondary_insurance_phone_number: Yup.string()
        .required('Secondary Insurance phone number is required')
        .test('valid-secondary-phone', 'Enter Valid Phone Number', (value) => !value || isValidPhoneNumber(value)),

      secondary_insurance_address: Yup.string()
        .required('Secondary Insurance Address is required')
        .test(
          'no-empty-space',
          'Secondary Insurance Address cannot be empty spaces',
          (value) => !value || !/^\s+$/.test(value),
        ),

      secondary_insurance_city: Yup.string().required('Secondary Insurance City is required'),

      secondary_insurance_state: Yup.string().required('Secondary Insurance State is required'),

      secondary_insurance_zip_code: Yup.string()
        .required('Secondary Insurance ZipCode is required')
        .test('valid-secondary-zip', 'Not valid zipcode', (value) => !value || isValidZipCode(value)),

      secondary_insurance_through: Yup.string()
        .required('Secondary Insurance is required')
        .notOneOf(['none'], 'Secondary Insurance is required'),
      secondary_insurance_first_name: Yup.string().when('secondary_insurance_through', {
        is: (val: string) => val !== 'none' && val.trim() !== '' && val !== undefined && val !== 'myself',
        then: Yup.string()
          .required('Secondary Insurance Holder First Name is required')
          .test('valid-name', 'Enter Valid First Name', (value) => !value || isValidName(value)),
        otherwise: Yup.string(),
      }),
      secondary_insurance_last_name: Yup.string().when('secondary_insurance_through', {
        is: (val: string) => val !== 'none' && val.trim() !== '' && val !== undefined && val !== 'myself',
        then: Yup.string()
          .required('Secondary Insurance Holder Last Name is required')
          .test('valid-name', 'Enter Valid Last Name', (value) => !value || isValidName(value)),
        otherwise: Yup.string(),
      }),
      secondary_insurance_date_of_birth: Yup.string()
        .when('secondary_insurance_through', {
          is: (val: string) => val !== 'none' && val.trim() !== '' && val !== undefined && val !== 'myself',
          then: Yup.string().required('Secondary Insurance Holder Date of birth is required'),
        })
        .test('valid-secondary-dob', 'Enter Valid Date MM/DD/YYYY', (value) => {
          if (!value) return true;
          const formattedDate = dayjs(value).format('MM/DD/YYYY');
          const [mm, dd, yyyy] = formattedDate.split('/');
          return isValidDate(formattedDate) && checkValidDate(mm, dd, yyyy);
        })
        .test('not-in-future-secondary', 'The Secondary Date of Birth can not be in the future.', (value) => {
          if (!value) return true;
          const [mm, dd, yyyy] = dayjs(value).format('MM/DD/YYYY').split('/');
          return checkFutureDate(mm, dd, yyyy);
        }),
      secondary_insurance_gender: Yup.string().when(['secondary_insurance_through'], {
        is: (val: string) => val !== 'none' && val.trim() !== '' && val !== undefined && val !== 'myself',
        then: Yup.string()
          .required('Secondary Insurance Holder Gender is required')
          .test('test-name', 'Enter Valid Gender', (value) => {
            if (value && !isValidName(value)) {
              return false;
            }
            return true;
          }),
        otherwise: Yup.string(),
      }),

      secondary_insurance_address1: Yup.string().when('secondary_insurance_through', {
        is: (val: string) => val !== 'none' && val.trim() !== '' && val !== undefined && val !== 'myself',
        then: Yup.string().required('Secondary Insurance Holder Address is required'),
      }),
      secondary_insurance_city1: Yup.string().when('secondary_insurance_through', {
        is: (val: string) => val !== 'none' && val.trim() !== '' && val !== undefined && val !== 'myself',
        then: Yup.string().required('Secondary Insurance Holder city Required'),
      }),
      secondary_insurance_state1: Yup.string().when('secondary_insurance_through', {
        is: (val: string) => val !== 'none' && val.trim() !== '' && val !== undefined && val !== 'myself',
        then: Yup.string().required('Secondary Insurance Holder state Required'),
      }),
      secondary_insurance_zip_code1: Yup.string()
        .when('secondary_insurance_through', {
          is: (val: string) => val !== 'none' && val.trim() !== '' && val !== undefined && val !== 'myself',
          then: Yup.string().required('Secondary Insurance Holder ZipCode is required'),
        })
        .test('valid-secondary-zip1', 'Not valid zipcode', (value) => !value || isValidZipCode(value)),
    });
  }
  if (paymentMethod === 'Self pay') {
    return Yup.object().shape({
      creditCardFullName: Yup.string().test('creditCardFullName', 'Full Name on card is required', function (value) {
        if (!value) {
          return false;
        }
        if (!isValidFullName(value)) {
          return this.createError({
            message: 'Please enter a valid Full Name',
          });
        }
        return true;
      }),
      creditCardAddress1: Yup.string().test('creditCardAddress1', 'Billing Address is required', (value) => {
        return !isSameAddress ? true : !!value?.trim();
      }),
      creditCardCity: Yup.string().test('creditCardCity', 'Billing City is required', (value) => {
        return !isSameAddress ? true : !!value?.trim();
      }),
      creditCardState: Yup.string().test('creditCardState', 'Billing State is required', (value) => {
        return !isSameAddress ? true : !!value?.trim();
      }),
      creditCardZipCode: Yup.string()
        .test('validZipCode', 'Not a valid zipcode', (value) => {
          if (isSameAddress) {
            if (value && !isValidZipCode(value)) {
              return false;
            }
          }
          return true;
        })
        .test('requiredZipCode', 'Billing Zip Code is required', (value) => {
          return !isSameAddress ? true : !!value?.trim();
        }),
    });
  }
};
